import { flexRender, type Row } from '@tanstack/react-table';
import { memo, type CSSProperties, type HTMLAttributes, type KeyboardEvent as ReactKeyboardEvent, type MouseEvent as ReactMouseEvent, type MutableRefObject, type ReactNode } from 'react';
import {
  consumeDataTableRowClickSuppression,
  isDataTableRowPointerBlocked,
} from '../useDataTableRowHorizontalScroll.js';
import { DataTableRowActionsContent } from './DataTableRowActions.js';
import { DataTableRowSelectControl } from './DataTableRowSelectControl.js';
import { getDataTableCellProps, wpPinToCellPin, type DataTableDensity } from './dataTableCell.js';
import { wrapStickyCellContent } from './dataTableStickyContent.js';
import { wpPinSide } from './useDataTableCore.js';
import type { Column, DataTableSelection } from './types.js';
import styles from './DataTable.module.css';
import { cx } from '../../utils/cx.js';

export type RenderDataTableDataRowOptions<T> = {
  tanRow: Row<T>;
  rowIndex: number;
  density: DataTableDensity;
  compactActions: boolean;
  rowLeadingActions?: (row: T) => ReactNode;
  rowActions?: (row: T) => ReactNode;
  selection?: DataTableSelection<T>;
  firstDataColumnKey?: string;
  onToggleRowSelection?: (key: string) => void;
  onRowClick?: (row: T) => void;
  rowProps?: (row: T) => HTMLAttributes<HTMLTableRowElement> | undefined;
  rowTrWrapper?: (
    row: T,
    tr: ReactNode,
    ctx: { rowIndex: number; sortableId: string },
  ) => ReactNode;
  rowKey: (row: T) => string;
  mergeCellStyles: (
    columnId: string,
    declarativeWidth?: string,
    isHeader?: boolean,
  ) => CSSProperties | undefined;
  suppressRowClickRef: MutableRefObject<boolean>;
  virtualRowStyle?: CSSProperties;
  measureRef?: (node: HTMLTableRowElement | null) => void;
  animateReceive?: boolean;
};

function DataTableDataRow<T>({
  tanRow,
  rowIndex,
  density: _density,
  compactActions,
  rowLeadingActions,
  rowActions,
  selection,
  firstDataColumnKey,
  onToggleRowSelection,
  onRowClick,
  rowProps,
  rowTrWrapper,
  rowKey,
  mergeCellStyles,
  suppressRowClickRef,
  virtualRowStyle,
  measureRef,
}: RenderDataTableDataRowOptions<T>): ReactNode {
  const row = tanRow.original;
  const extra = rowProps?.(row);
  const extraOnClick = extra?.onClick;
  const mergedOnClick =
    onRowClick || extraOnClick
      ? (e: ReactMouseEvent<HTMLTableRowElement>) => {
          extraOnClick?.(e);
          if (consumeDataTableRowClickSuppression(suppressRowClickRef)) return;
          if (!onRowClick || e.defaultPrevented) return;
          const rowEl = e.currentTarget;
          const t = e.target;
          if (!t || !(t instanceof Element)) return;
          if (isDataTableRowPointerBlocked(t)) return;
          for (const root of rowEl.querySelectorAll('[data-datatable-rowclick-ignore]')) {
            if (root.contains(t) || (t !== rowEl && t.contains(root))) return;
          }
          onRowClick(row);
        }
      : undefined;
  const mergedOnKeyDown = onRowClick
    ? (e: ReactKeyboardEvent<HTMLTableRowElement>) => {
        extra?.onKeyDown?.(e);
        if (e.defaultPrevented) return;
        if (e.key !== 'Enter' && e.key !== ' ') return;
        const t = e.target;
        if (t instanceof Element && isDataTableRowPointerBlocked(t)) return;
        e.preventDefault();
        onRowClick(row);
      }
    : extra?.onKeyDown;
  const { onKeyDown: _extraKeyDown, onClick: _extraOnClick, ...extraRest } = extra ?? {};
  const rowSelectKey = rowKey(row);

  const tr = (
    <tr
      key={tanRow.id}
      ref={measureRef}
      data-index={rowIndex}
      {...extraRest}
      tabIndex={onRowClick ? 0 : extra?.tabIndex}
      className={cx(styles.tr, onRowClick && styles.trClickable, extra?.className)}
      style={virtualRowStyle}
      onClick={mergedOnClick}
      onKeyDown={mergedOnKeyDown}
    >
      {tanRow.getVisibleCells().map((cell) => {
        if (cell.column.id === '__actions') {
          return (
            <DataTableRowActionsContent
              key={cell.id}
              leading={rowLeadingActions?.(row)}
              menu={rowActions?.(row)}
              compact={compactActions}
              stickyEnd={cell.column.getIsPinned() === 'right'}
              columnWidthStyle={mergeCellStyles('__actions', undefined, false)}
            />
          );
        }
        const wpCol = (cell.column.columnDef.meta as { wpColumn?: Column<T> } | undefined)?.wpColumn;
        const pin = wpPinSide(cell.column.getIsPinned());
        const isLeadCol = Boolean(selection && firstDataColumnKey && cell.column.id === firstDataColumnKey);
        const selectable = selection?.isRowSelectable?.(row) !== false;
        const bodyCell = getDataTableCellProps({
          element: 'td',
          part: 'body',
          pin: wpPinToCellPin(pin),
          extraClassName: cx(wpCol?.columnClassName, isLeadCol && styles.tdHasRowSelect),
        });
        const cellContent = flexRender(cell.column.columnDef.cell, cell.getContext());
        return (
          <td
            key={cell.id}
            {...bodyCell}
            style={{
              ...mergeCellStyles(cell.column.id, wpCol?.width),
              ...(wpCol?.columnStyle ?? {}),
            }}
          >
            {isLeadCol ? (
              <DataTableRowSelectControl
                checked={selection?.selectedKeys.has(rowSelectKey) ?? false}
                disabled={!selectable}
                ariaLabel={`Sélectionner la ligne ${rowSelectKey}`}
                onChange={() => onToggleRowSelection?.(rowSelectKey)}
              />
            ) : null}
            {wrapStickyCellContent(cellContent, wpPinToCellPin(pin), {
              ellipsis: wpCol?.stickyContentEllipsis !== false,
            })}
          </td>
        );
      })}
    </tr>
  );

  if (rowTrWrapper) {
    return rowTrWrapper(row, tr, { rowIndex, sortableId: rowKey(row) });
  }
  return tr;
}

export const renderDataTableDataRow = memo(DataTableDataRow) as typeof DataTableDataRow;
